import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { View, MapCoordinates } from '../../types';
import { useMapData } from '../../hooks/useMapData';

// Fix Leaflet default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons based on status and type
const createCustomIcon = (type: 'source' | 'destination', status: string) => {
  const iconColor = status === 'confirmed' ? '#22c55e' : 
                   status === 'likely' ? '#3b82f6' : 
                   status === 'plausible' ? '#f59e0b' : '#ef4444';
  
  const iconHtml = `
    <div style="
      width: 20px; 
      height: 20px; 
      background: ${iconColor}; 
      border: 2px solid white; 
      border-radius: ${type === 'source' ? '50%' : '20%'}; 
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 9px;
    ">
      ${type === 'source' ? 'S' : 'D'}
    </div>
  `;

  return L.divIcon({
    html: iconHtml,
    className: 'custom-marker',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -10]
  });
};

// Performance optimization for large datasets
const BATCH_SIZE = 100;
const RENDER_DELAY = 10;

interface InteractiveMapLeafletProps {
  views: View[];
  onViewSelect?: (view: View) => void;
  className?: string;
  height?: string;
  initialCenter?: MapCoordinates;
  initialZoom?: number;
}

const DEFAULT_CENTER: MapCoordinates = { lat: 40.7128, lng: -74.0060 }; // New York City

// Mock data for development
const MOCK_VIEWS: View[] = [
  {
    id: '1',
    distance: '25.4',
    distanceKm: 25.4,
    distanceMi: 15.78,
    source: 'Mount Washington',
    state: 'New Hampshire',
    country: 'United States',
    destination: 'Mount Katahdin',
    destinationState: 'Maine',
    destinationCountry: 'United States',
    status: 'confirmed',
    image: 'test-image-1.jpg',
    color: '#22c55e',
    type: 'mountain'
  },
  {
    id: '2',
    distance: '142.8',
    distanceKm: 142.8,
    distanceMi: 88.73,
    source: 'Empire State Building',
    state: 'New York',
    country: 'United States',
    destination: 'One World Trade Center',
    destinationState: 'New York',
    destinationCountry: 'United States',
    status: 'confirmed',
    image: 'test-image-2.jpg',
    color: '#3b82f6',
    type: 'urban'
  },
  {
    id: '3',
    distance: '89.7',
    distanceKm: 89.7,
    distanceMi: 55.74,
    source: 'Golden Gate Bridge',
    state: 'California',
    country: 'United States',
    destination: 'Statue of Liberty',
    destinationState: 'New York',
    destinationCountry: 'United States',
    status: 'likely',
    image: 'test-image-3.jpg',
    color: '#f59e0b',
    type: 'landmark'
  }
];

export const InteractiveMapLeaflet: React.FC<InteractiveMapLeafletProps> = ({
  views,
  onViewSelect,
  className = '',
  height = '600px',
  initialCenter = DEFAULT_CENTER,
  initialZoom = 5
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const polylinesRef = useRef<L.Polyline[]>([]);
  
  const [selectedView, setSelectedView] = useState<View | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [renderStartTime, setRenderStartTime] = useState<number>(0);

  // Use mock data if no views provided (development mode)
  const displayViews = views.length > 0 ? views : MOCK_VIEWS;
  
  // Use the enhanced map data hook
  const { markers, connections, isLoading, error, bounds, stats } = useMapData(displayViews);

  // Batch rendering function for performance with large datasets
  const renderMarkersInBatches = useCallback(async (leafletMarkers: L.Marker[], markersLayer: L.LayerGroup) => {
    const batches = [];
    for (let i = 0; i < leafletMarkers.length; i += BATCH_SIZE) {
      batches.push(leafletMarkers.slice(i, i + BATCH_SIZE));
    }

    for (const batch of batches) {
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          batch.forEach(marker => markersLayer.addLayer(marker));
          setTimeout(resolve, RENDER_DELAY);
        });
      });
    }
  }, []);

  // Callback to handle view selection
  const handleViewSelect = useCallback((view: View) => {
    setSelectedView(view);
    onViewSelect?.(view);
  }, [onViewSelect]);

  // Initialize Leaflet map
  useEffect(() => {
    if (!mapRef.current || leafletMapRef.current) return;

    try {
      // Create map with optimized settings for performance
      const map = L.map(mapRef.current, {
        center: [initialCenter.lat, initialCenter.lng],
        zoom: initialZoom,
        minZoom: 2,
        maxZoom: 18,
        zoomControl: true,
        attributionControl: true,
        preferCanvas: true, // Use canvas for better performance
        renderer: L.canvas(), // Explicit canvas renderer
        wheelPxPerZoomLevel: 60, // Smoother zoom
        zoomAnimationThreshold: 4
      });

      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
        updateWhenIdle: false,
        updateWhenZooming: false,
        keepBuffer: 2
      }).addTo(map);

      // Create layer group for markers (no clustering)
      const markersLayer = L.layerGroup();
      map.addLayer(markersLayer);

      leafletMapRef.current = map;
      markersLayerRef.current = markersLayer;
      setIsMapReady(true);

      // Leaflet map initialized successfully

    } catch (err) {
      console.error('❌ Failed to initialize Leaflet map:', err);
      setMapError('Failed to initialize map');
    }

    // Cleanup
    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        markersLayerRef.current = null;
        setIsMapReady(false);
      }
    };
  }, [initialCenter.lat, initialCenter.lng, initialZoom]);

  // Update markers and connections when data changes
  useEffect(() => {
    if (!leafletMapRef.current || !markersLayerRef.current || !isMapReady || isLoading) return;

    const map = leafletMapRef.current;
    const markersLayer = markersLayerRef.current;
    
    // Track rendering performance
    setRenderStartTime(Date.now());

    // Clear existing markers and polylines
    markersLayer.clearLayers();
    polylinesRef.current.forEach(polyline => map.removeLayer(polyline));
    polylinesRef.current = [];

    // Add connection lines first (so they appear behind markers)
    connections.forEach((connection, index) => {
      const lineColor = connection.status === 'confirmed' ? '#22c55e' : 
                       connection.status === 'likely' ? '#3b82f6' : 
                       connection.status === 'plausible' ? '#f59e0b' : '#ef4444';
      
      const polyline = L.polyline([
        [connection.sourcePosition.lat, connection.sourcePosition.lng],
        [connection.destinationPosition.lat, connection.destinationPosition.lng]
      ], {
        color: lineColor,
        weight: 2,
        opacity: 0.7,
        interactive: true
      });

      // Add test attributes for coordinate verification
      const polylineElement = polyline as any;
      polylineElement.testId = `connection-line-${index}`;
      polylineElement.sourceCoords = connection.sourcePosition;
      polylineElement.destCoords = connection.destinationPosition;

      polyline.bindPopup(`
        <div class="text-sm" data-testid="connection-popup-${index}">
          <h3 class="font-bold">${connection.viewData.source} → ${connection.viewData.destination}</h3>
          <p>Distance: ${connection.distance.toFixed(1)} km</p>
          <p>Status: ${connection.status}</p>
          <div class="text-xs text-gray-500 mt-2">
            Source: ${connection.sourcePosition.lat.toFixed(6)}, ${connection.sourcePosition.lng.toFixed(6)}<br>
            Dest: ${connection.destinationPosition.lat.toFixed(6)}, ${connection.destinationPosition.lng.toFixed(6)}
          </div>
        </div>
      `);

      polyline.on('click', () => {
        handleViewSelect(connection.viewData);
      });

      polyline.addTo(map);
      polylinesRef.current.push(polyline);
    });

    // Create markers with performance optimization
    const leafletMarkers = markers.map((marker, index) => {
      const leafletMarker = L.marker(
        [marker.position.lat, marker.position.lng],
        { 
          icon: createCustomIcon(marker.type, marker.status),
          riseOnHover: true
        }
      );

      // Add test attributes for coordinate verification
      const markerElement = leafletMarker as any;
      markerElement.testId = `marker-${marker.title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
      markerElement.coords = marker.position;
      markerElement.viewId = marker.viewId;

      leafletMarker.bindPopup(`
        <div class="text-sm" data-testid="marker-popup-${index}">
          <h3 class="font-bold">${marker.title}</h3>
          <p class="text-gray-600">${marker.location}</p>
          <p class="capitalize">
            <span class="inline-block w-2 h-2 rounded-full mr-1" style="background-color: ${marker.color}"></span>
            ${marker.status} ${marker.type}
          </p>
          <div class="text-xs text-gray-500 mt-2">
            Coordinates: ${marker.position.lat.toFixed(6)}, ${marker.position.lng.toFixed(6)}
          </div>
        </div>
      `);

      leafletMarker.on('click', () => {
        const view = displayViews.find(v => v.id === marker.viewId);
        if (view) {
          handleViewSelect(view);
        }
      });

      return leafletMarker;
    });

    // Use batch rendering for large datasets
    if (leafletMarkers.length > 500) {
      // Batch rendering markers for performance
      renderMarkersInBatches(leafletMarkers, markersLayer);
    } else {
      // Direct rendering for smaller datasets
      leafletMarkers.forEach(marker => markersLayer.addLayer(marker));
    }

    // Fit bounds to show all markers
    if (markers.length > 0 && bounds) {
      const leafletBounds = L.latLngBounds([
        [bounds.south, bounds.west],
        [bounds.north, bounds.east]
      ]);
      
      map.fitBounds(leafletBounds, { 
        padding: [20, 20],
        maxZoom: 12 
      });
    }

    // Log performance metrics
    const renderTime = Date.now() - renderStartTime;
          // Updated map with markers and connections

  }, [markers, connections, isMapReady, isLoading, bounds, displayViews, handleViewSelect, renderStartTime, renderMarkersInBatches]);

  // Error state
  if (error || mapError) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ height }}
        data-testid="map-error-message"
      >
        <div className="text-center p-6">
          <p className="text-red-600 font-medium mb-2">Map Error</p>
          <p className="text-gray-600 text-sm">{error || mapError}</p>
          <button 
            onClick={() => {
              setMapError(null);
              window.location.reload();
            }}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ height }}
        data-testid="map-loading"
      >
        <div className="text-center p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-semibold">Getting real coordinates...</p>
          <p className="text-sm text-gray-500 mt-2">
            Processing {displayViews.length} views with intelligent batching
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Using backend coordinates when available for faster processing
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative rounded-lg overflow-hidden ${className}`}
      style={{ height }}
      data-testid="leaflet-map"
    >
      {/* Leaflet Map Container */}
      <div 
        ref={mapRef} 
        style={{ height: '100%', width: '100%' }}
        className="leaflet-container"
        data-testid="interactive-map-container"
      />

      {/* Enhanced Stats Panel */}
      <div className="absolute top-4 right-4 bg-gray-700 p-3 rounded-lg shadow-lg border max-w-xs z-[1000]" data-testid="map-stats-panel">
        <h4 className="font-semibold text-sm mb-2">Map Statistics</h4>
        <div className="text-xs space-y-1">
          <div className="flex justify-between">
            <span>Views:</span>
            <span data-testid="stats-views-count">{displayViews.length}</span>
          </div>
          <div className="flex justify-between">
            <span>Markers:</span>
            <span data-testid="stats-markers-count">{markers.length}</span>
          </div>
          <div className="flex justify-between">
            <span>Connections:</span>
            <span data-testid="stats-connections-count">{connections.length}</span>
          </div>
          <div className="flex justify-between">
            <span>Total Distance:</span>
            <span data-testid="stats-total-distance">{stats.totalDistance.toFixed(1)} km</span>
          </div>
          <div className="flex justify-between">
            <span>Average:</span>
            <span data-testid="stats-average-distance">{stats.averageDistance.toFixed(1)} km</span>
          </div>
        </div>
        
        {/* Status Distribution */}
        <div className="mt-2 pt-2 border-t">
          <div className="text-xs font-medium mb-1">Status Distribution:</div>
          {Object.entries(stats.statusCounts).map(([status, count]) => (
            <div key={status} className="flex justify-between text-xs">
              <span className="capitalize">{status}:</span>
              <span data-testid={`stats-status-${status}`}>{count}</span>
            </div>
          ))}
        </div>

        {/* Performance Indicator */}
        {markers.length > 500 && (
          <div className="mt-2 pt-2 border-t">
            <div className="text-xs font-medium text-green-600">
              ⚡ Batch Rendering ({markers.length} markers)
            </div>
          </div>
        )}
      </div>

      {/* Development Info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute bottom-4 right-4 bg-black bg-opacity-75 text-white text-xs p-2 rounded z-[1000]">
          <div>Views: {displayViews.length}</div>
          <div>Markers: {markers.length}</div>
          <div>Connections: {connections.length}</div>
          <div>Map: {isMapReady ? 'Ready' : 'Loading'}</div>
          <div>Mode: {views.length > 0 ? 'Real Data' : 'Mock Data'}</div>
          <div>Leaflet: v{L.version}</div>
          <div>Markers Layer: {markersLayerRef.current ? 'Active' : 'Inactive'}</div>
          {bounds && (
            <div>Bounds: {bounds.north.toFixed(2)}, {bounds.south.toFixed(2)}</div>
          )}
        </div>
      )}

      {/* Selected View Info */}
      {selectedView && (
        <div className="absolute top-4 left-4 bg-white p-3 rounded-lg shadow-lg border max-w-xs z-[1000]" data-testid="selected-view-info">
          <h4 className="font-semibold text-sm mb-1">{selectedView.source}</h4>
          <p className="text-xs text-gray-600 mb-2">to {selectedView.destination}</p>
          <div className="flex justify-between text-xs mb-2">
            <span>Distance: {selectedView.distance} km</span>
            <span className="capitalize">{selectedView.status}</span>
          </div>
          {selectedView.type && (
            <div className="text-xs text-gray-500 mb-2">
              Type: {Array.isArray(selectedView.type) ? selectedView.type.join(', ') : selectedView.type}
            </div>
          )}
          <button
            onClick={() => setSelectedView(null)}
            className="mt-2 text-xs text-blue-600 hover:text-blue-800"
            data-testid="close-selected-view"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}; 